import { BoundSelectField, Css, FormPageLayout, MultiSelectField, SelectField, Switch } from "@homebound/beam";
import { type ObjectConfig, type ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { formatToPrice } from "src/components";
import {
  ChangeEventBudgetPhase,
  ChangeEventType,
  CheckoutConfigOptionGroupStep,
  ProjectReadyPlanConfigOrientation,
  ReadyPlanType,
  type SaveProjectReadyPlanConfigsInput,
  Stage,
} from "src/generated/graphqlTypes";
import { type ProductOfferingConfigApplyParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";
import { queriesResult } from "src/utils";
import {
  type ApplyProductOfferingConfig_ComputeResultFragment,
  type ApplyProductOfferingConfig_ProjectFragment,
  type ApplyProductOfferingConfig_SelectedQuery,
  type ApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfig_ComputeLazyQuery,
  useApplyProductOfferingConfig_SelectedQuery,
  useApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfigPageMetaQuery,
  useApplyProductOfferingConfigsMutation,
  useCreateProductOfferingConfigChangeEventMutation,
} from "./ApplyProductOfferingConfigPage.generated";

export function ApplyProductOfferingConfigPage() {
  const { configId, projectId } = useParams<ProductOfferingConfigApplyParams>();
  const setupQuery = useApplyProductOfferingConfigPageMetaQuery({ variables: { projectId } });
  const configQuery = useApplyProductOfferingConfig_SelectedQuery({
    variables: {
      id: configId,
      skipCheckoutConfig: !configId.startsWith("ccfg:"),
      skipProductOfferingConfig: !configId.startsWith("poc:"),
      skipProjectReadyPlanConfig: !configId.startsWith("prpc:"),
    },
  });
  const selectedConfig = getProductOfferingSelection(configId, configQuery.data, setupQuery.data?.project);

  const metaDataQuery = useApplyProductOfferingConfigMetaQuery({
    variables: {
      productOfferingId: selectedConfig.productOfferingId ?? "",
      productOfferingVersionId: selectedConfig.productOfferingVersionId,
    },
    fetchPolicy: "no-cache",
    skip: setupQuery.loading || configQuery.loading || !selectedConfig.productOfferingId,
  });

  return queriesResult([setupQuery, configQuery, metaDataQuery] as const, {
    data: (setupData, configData, metaData) => (
      <ApplyProductOfferingConfigPageView
        metaData={metaData}
        configData={configData}
        productOfferingId={selectedConfig.productOfferingId!}
        initialReadyPlanOptionIds={selectedConfig.readyPlanOptionIds}
        project={setupData.project}
        changeEventReasons={setupData.changeEventReasons}
      />
    ),
  });
}

type ApplyOfferingConfigPageProps = {
  metaData: ApplyProductOfferingConfigMetaQuery;
  configData: ApplyProductOfferingConfig_SelectedQuery;
  productOfferingId: string;
  initialReadyPlanOptionIds: string[];
  project: ApplyProductOfferingConfig_ProjectFragment;
  changeEventReasons: { id: string; code: string }[];
};

type ReadyPlanOption = ApplyProductOfferingConfigMetaQuery["productOffering"]["options"][number];
type ReadyPlanOptionGroup = ReadyPlanOption["optionGroup"] & { readyPlanOptions: ReadyPlanOption[] };
type OptionWithGroupOrder = { id: string; displayName: string; groupOrder: number };

export function ApplyProductOfferingConfigPageView(props: ApplyOfferingConfigPageProps) {
  const history = useHistory();
  const { metaData, configData, productOfferingId, project, changeEventReasons, initialReadyPlanOptionIds } = props;
  const constructionStage = project.stages.find((s) => s.stage.code === Stage.Construction);
  const archivedOptions = useMemo(
    () => getArchivedOptions(metaData.productOffering.options),
    [metaData.productOffering.options],
  );
  const allOptions = useMemo(
    () => getActiveOptions(metaData.productOffering.options),
    [metaData.productOffering.options],
  );

  const formState = useFormState({
    config: formConfig,
    init: { input: props, map: (queryResults) => mapConfigToForm(queryResults) },
  });
  const [applyProductOfferingConfigs] = useApplyProductOfferingConfigsMutation();
  const [createProductOfferingConfigChangeEvent] = useCreateProductOfferingConfigChangeEventMutation();
  const [computeAppliedConfig, computeQuery] = useApplyProductOfferingConfig_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(initialReadyPlanOptionIds);
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>([]);
  const [appliedComputeResult, setAppliedComputeResult] = useState<ApplyProductOfferingConfig_ComputeResultFragment>();

  const optionGroups = useMemo(() => createOptionGroups(allOptions), [allOptions]);
  const selectedOptionIdByGroupId = useMemo(
    () => createSelectedOptionIdByGroupId(allOptions, selectedOptionIds),
    [allOptions, selectedOptionIds],
  );
  const disabledOptionIds = useMemo(
    () => getDisabledOptionIds(allOptions, selectedOptionIds),
    [allOptions, selectedOptionIds],
  );
  const appliedOptionCostById = useMemo(
    () => (appliedComputeResult ? createOptionCostById(appliedComputeResult) : new Map<string, number>()),
    [appliedComputeResult],
  );
  const checkoutStepSections = useMemo(() => createCheckoutStepSections(optionGroups), [optionGroups]);
  const archivedOptionIds = useMemo(() => archivedOptions.map((option) => option.id), [archivedOptions]);
  const selectedArchivedOptionIds = useMemo(
    () => selectedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [archivedOptionIds, selectedOptionIds],
  );
  const appliedArchivedOptionIds = useMemo(
    () => appliedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [appliedOptionIds, archivedOptionIds],
  );
  const showArchivedSection =
    archivedOptions.nonEmpty && (selectedArchivedOptionIds.nonEmpty || appliedArchivedOptionIds.nonEmpty);

  const loadOptionIds = useCallback(
    (optionIds: string[]) => {
      setSelectedOptionIds(optionIds);
      formState.readyPlanOptions.set(toFormReadyPlanOptions(optionIds));
    },
    [formState],
  );

  const updateAppliedConfig = useCallback(
    async (optionIds: string[], originalOptionIds?: string[]) => {
      const { data } = await computeAppliedConfig({
        variables: {
          readyPlanId: productOfferingId,
          readyPlanOptionIds: optionIds,
          originalReadyPlanOptionIds: originalOptionIds?.nonEmpty ? originalOptionIds : undefined,
        },
      });
      const result = data?.computeReadyPlanCost;
      if (!result) return;

      // computeReadyPlanCost can omit archived options, but keep them selected until the user explicitly removes them.
      const submittedArchivedOptionIds = optionIds.filter((optionId) => archivedOptionIds.includes(optionId));
      const resolvedOptionIds = [
        ...result.readyPlanOptions.map((option) => option.id),
        ...submittedArchivedOptionIds,
      ].unique();
      loadOptionIds(resolvedOptionIds);
      setAppliedOptionIds(resolvedOptionIds);
      setAppliedComputeResult(result);
    },
    [archivedOptionIds, computeAppliedConfig, loadOptionIds, productOfferingId],
  );

  // Only hydrate from the source config once; duplicate effect runs would re-call compute and could reset user edits.
  const didUpdateInitialConfig = useRef(false);
  useEffect(() => {
    if (didUpdateInitialConfig.current) return;
    didUpdateInitialConfig.current = true;
    void updateAppliedConfig(initialReadyPlanOptionIds);
  }, [initialReadyPlanOptionIds, updateAppliedConfig]);

  const handleOptionGroupSelect = useCallback(
    (optionGroup: ReadyPlanOptionGroup, optionId: string | undefined) => {
      const nextOptionIds = updateSelectedOptionIdsForGroup(allOptions, selectedOptionIds, optionGroup.id, optionId);
      loadOptionIds(nextOptionIds);
    },
    [allOptions, loadOptionIds, selectedOptionIds],
  );
  const handleArchivedOptionsSelect = useCallback(
    (archivedOptionIds: string[]) => {
      const archivedOptionIdSet = new Set(archivedOptions.map((option) => option.id));
      loadOptionIds([
        ...selectedOptionIds.filter((optionId) => !archivedOptionIdSet.has(optionId)),
        ...archivedOptionIds,
      ]);
    },
    [archivedOptions, loadOptionIds, selectedOptionIds],
  );

  const hasUnappliedSelections = !selectedOptionIds.hasSameElements(appliedOptionIds);
  const configurationErrors = appliedComputeResult?.configurationErrors ?? [];
  const missingRequiredRpos = useMemo(
    () => getMissingRequiredRpos(optionGroups, appliedOptionIds),
    [appliedOptionIds, optionGroups],
  );
  const disableApply =
    (computeQuery.loading && "Updating selections") ||
    (!appliedComputeResult && "Update selections before applying") ||
    (hasUnappliedSelections && "Update selections before applying") ||
    (configurationErrors.nonEmpty && "Resolve configuration errors before applying") ||
    missingRequiredRpos;
  const disableUpdate =
    (computeQuery.loading && "Updating selections") ||
    (!!appliedComputeResult && !hasUnappliedSelections && "Selections are already up to date");
  const cancelLabel = configurationErrors.nonEmpty ? (
    <>
      <span data-testid="configErrors">{configurationErrors.length} Configuration Errors</span> · Cancel
    </>
  ) : (
    "Cancel"
  );

  const redirectRoute = createChangeEventsUrl(props.project.id);

  return (
    <FormPageLayout
      formSections={[
        {
          title: "Details",
          rows: [
            {
              reactNodeSubtitle:
                "Select options, click Update to resolve the configuration, then Apply the updated configuration to the project.",
            },
            {
              reactNode: (
                <div css={Css.df.fdc.gap2.$}>
                  <BoundSelectField
                    label="Orientation"
                    field={formState.orientation}
                    options={orientationOptions}
                    getOptionValue={(o) => o.code}
                    getOptionLabel={(o) => o.name}
                  />
                  <div css={Css.df.aic.gap1.flexWrap("wrap").$}>
                    {hasUnappliedSelections && (
                      <span css={Css.sm.gray700.$}>Selections have not been updated yet.</span>
                    )}
                  </div>
                  {computeQuery.error && (
                    <div css={Css.sm.red600.$}>Failed to update: {computeQuery.error.message}</div>
                  )}
                </div>
              ),
            },
          ],
        },
        ...checkoutStepSections.map((section) => ({
          title: section.title,
          rows: [
            {
              reactNode: (
                <div css={Css.df.fdc.gap2.$}>
                  {section.optionGroups.map((optionGroup) => (
                    <ReadyPlanOptionGroupSelect
                      key={optionGroup.id}
                      optionGroup={optionGroup}
                      value={selectedOptionIdByGroupId.get(optionGroup.id)}
                      selectedOptionIds={selectedOptionIds}
                      appliedOptionIds={appliedOptionIds}
                      appliedOptionCostById={appliedOptionCostById}
                      disabledOptionIds={disabledOptionIds}
                      onSelect={(optionId) => handleOptionGroupSelect(optionGroup, optionId)}
                    />
                  ))}
                </div>
              ),
            },
          ],
        })),
        ...(showArchivedSection
          ? [
              {
                title: "Archived",
                rows: [
                  {
                    reactNode: (
                      <MultiSelectField
                        data-testid="archivedOptions"
                        label="Archived Options"
                        values={selectedArchivedOptionIds}
                        options={archivedOptions}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
                        onSelect={handleArchivedOptionsSelect}
                        nothingSelectedText="None"
                        autoSort={false}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ]}
      breadCrumb={{ label: "Change Events / Create New", href: "" }}
      formState={formState}
      pageTitle="Create Change Event"
      submitAction={{
        label: "Apply",
        onClick: async () => {
          if (constructionStage?.hasSignedContract) {
            await createProductOfferingConfigChangeEvent({
              variables: {
                input: {
                  projectStageId: constructionStage?.id,
                  readyPlanId: productOfferingId,
                  originalReadyPlanId: project.readyPlanConfig?.readyPlan?.id,
                  originalReadyPlanOptions: project.readyPlanConfig?.readyPlanOptions.map((rpo) => ({
                    readyPlanOptionId: rpo.id,
                  })),
                  proposedReadyPlanOptions: appliedOptionIds.map((readyPlanOptionId) => ({ readyPlanOptionId })),
                  proposedOrientation: formState.value.orientation,
                  checkoutConfigId: configData.checkoutConfig?.id,
                  title: `Change Configuration of POF ${productOfferingId} on ${project.name}`,
                  reasonId: changeEventReasons.find((reason) => reason.code === "RFP_EXECUTION")?.id ?? "cer:1",
                  type: ChangeEventType.External,
                  budgetPhase: ChangeEventBudgetPhase.Prebid,
                },
              },
            });
          } else {
            await applyProductOfferingConfigs({
              variables: {
                input: mapFormToApplyProductConfigConfigInput(
                  project.readyPlanConfig?.id!,
                  formState,
                  appliedOptionIds,
                ),
              },
            });
          }

          return history.push(redirectRoute);
        },
        disabled: disableApply,
      }}
      tertiaryAction={{
        label: cancelLabel,
        onClick: redirectRoute,
        tooltip: configurationErrors.nonEmpty ? (
          <ConfigurationErrorsTooltip configurationErrors={configurationErrors} />
        ) : undefined,
      }}
      cancelAction={{
        label: computeQuery.loading ? "Updating..." : "Update",
        onClick: () => void updateAppliedConfig(selectedOptionIds, appliedOptionIds),
        disabled: disableUpdate,
      }}
    />
  );
}

type ReadyPlanOptionGroupSelectProps = {
  optionGroup: ReadyPlanOptionGroup;
  value: string | undefined;
  selectedOptionIds: string[];
  appliedOptionIds: string[];
  appliedOptionCostById: Map<string, number>;
  disabledOptionIds: string[];
  onSelect: (optionId: string | undefined) => void;
};

/** Renders one input for an RPOG. */
function ReadyPlanOptionGroupSelect(props: ReadyPlanOptionGroupSelectProps) {
  const {
    optionGroup,
    value,
    selectedOptionIds,
    appliedOptionIds,
    appliedOptionCostById,
    disabledOptionIds,
    onSelect,
  } = props;
  const options = getOptionsForSelect(optionGroup, selectedOptionIds, appliedOptionIds);
  const option = options[0];

  if (optionGroup.isSingleOptionGroup && option) {
    const selected = value === option.id;
    return (
      <div css={Css.df.fdc.gap1.$} data-testid="readyPlanOptionToggle">
        <div css={Css.xsSb.gray700.$}>{`${optionGroup.name}${optionGroup.required ? " *" : ""}`}</div>
        <Switch
          label={getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
          selected={selected}
          disabled={!selected && disabledOptionIds.includes(option.id)}
          onChange={(checked) => onSelect(checked ? option.id : undefined)}
        />
      </div>
    );
  }

  return (
    <SelectField
      data-testid="readyPlanOption"
      label={`${optionGroup.name}${optionGroup.required ? " *" : ""}`}
      value={value}
      options={options}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
      onSelect={onSelect}
      nothingSelectedText="—"
      disabledOptions={disabledOptionIds}
      autoSort={false}
    />
  );
}

/** Displays configuration errors returned by computeReadyPlanCost. */
function ConfigurationErrorsTooltip(props: { configurationErrors: string[] }) {
  const { configurationErrors } = props;
  return (
    <ul css={Css.pl2.$}>
      {configurationErrors.map((error) => (
        <li key={error}>{error}</li>
      ))}
    </ul>
  );
}

type ApplyProductOfferingConfigForm = {
  productOfferingId: string | null | undefined;
  readyPlanOptions: { readyPlanOptionId: string | null | undefined }[];
  orientation: ProjectReadyPlanConfigOrientation | null | undefined;
};

const orientationOptions = [
  { code: ProjectReadyPlanConfigOrientation.Left, name: "Left" },
  { code: ProjectReadyPlanConfigOrientation.Right, name: "Right" },
];

export const formConfig: ObjectConfig<ApplyProductOfferingConfigForm> = {
  productOfferingId: { type: "value", rules: [required] },
  readyPlanOptions: { type: "list", config: { readyPlanOptionId: { type: "value" } } },
  orientation: { type: "value" },
};

export function mapConfigToForm(queryResults: ApplyOfferingConfigPageProps): ApplyProductOfferingConfigForm {
  const { configData, project, productOfferingId, initialReadyPlanOptionIds } = queryResults;
  return {
    productOfferingId,
    readyPlanOptions: toFormReadyPlanOptions(initialReadyPlanOptionIds),
    orientation: getInitialOrientation(configData, project),
  };
}

export function mapFormToApplyProductConfigConfigInput(
  structureProjectConfigId: string,
  formState: ObjectState<ApplyProductOfferingConfigForm>,
  readyPlanOptionIds: string[],
): SaveProjectReadyPlanConfigsInput {
  const { productOfferingId, orientation } = formState.value;
  return {
    projectReadyPlanConfigs: [
      {
        id: structureProjectConfigId,
        readyPlanId: productOfferingId,
        orientation,
        options: readyPlanOptionIds.map((readyPlanOptionId) => ({ readyPlanOptionId })),
      },
    ],
    overwriteProgramDataWithComputed: true,
  };
}

type ProductOfferingSelection = {
  productOfferingId?: string;
  productOfferingVersionId?: string;
  readyPlanOptionIds: string[];
};

/** Finds the source product offering and option ids for the selected config id. */
function getProductOfferingSelection(
  configId: string,
  configData: ApplyProductOfferingConfig_SelectedQuery | undefined,
  project: ApplyProductOfferingConfig_ProjectFragment | undefined,
): ProductOfferingSelection {
  if (configId.startsWith("poc:")) {
    const productOfferingConfig = configData?.productOfferingConfig;
    return {
      productOfferingId: productOfferingConfig?.productOffering.id,
      productOfferingVersionId: productOfferingConfig?.productOffering.aggregateCheckout?.id,
      readyPlanOptionIds: productOfferingConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
    };
  }

  if (configId.startsWith("ccfg:")) {
    const checkoutConfig = configData?.checkoutConfig;
    if (!checkoutConfig || !project) return { readyPlanOptionIds: [] };

    const isAdu = project.__typename === "Structure" && project.planType?.code === ReadyPlanType.Adu;
    const productOfferingId = isAdu ? checkoutConfig.aduProductOffering?.id : checkoutConfig.productOffering?.id;
    return {
      productOfferingId,
      productOfferingVersionId: isAdu
        ? checkoutConfig.aduProductOffering?.aggregateCheckout?.id
        : checkoutConfig.productOffering?.aggregateCheckout?.id,
      readyPlanOptionIds:
        checkoutConfig.optionGroups
          .filter((og) => og.selectedOption?.option.readyPlan.id === productOfferingId)
          .map((og) => og.selectedOption?.option.id)
          .compact() ?? [],
    };
  }

  const projectReadyPlanConfig = configData?.projectReadyPlanConfig;
  const readyPlan = projectReadyPlanConfig?.readyPlan;
  return {
    productOfferingId: projectReadyPlanConfig?.readyPlan?.id,
    productOfferingVersionId: readyPlan?.__typename === "ProductOffering" ? readyPlan.aggregateCheckout?.id : undefined,
    readyPlanOptionIds: projectReadyPlanConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
  };
}

/** Determines the orientation that should be shown on initial load. */
function getInitialOrientation(
  configData: ApplyProductOfferingConfig_SelectedQuery,
  project: ApplyProductOfferingConfig_ProjectFragment,
): ProjectReadyPlanConfigOrientation {
  if (configData.checkoutConfig) {
    return configData.checkoutConfig.isReversed
      ? ProjectReadyPlanConfigOrientation.Left
      : ProjectReadyPlanConfigOrientation.Right;
  }
  if (configData.projectReadyPlanConfig) return configData.projectReadyPlanConfig.orientation;
  return project.readyPlanConfig?.orientation ?? ProjectReadyPlanConfigOrientation.Right;
}

/** Returns active options from active RPOGs. */
function getActiveOptions(options: ReadyPlanOption[]) {
  return options.filter((option) => option.active && option.optionGroup.active);
}

/** Returns inactive options or options from inactive RPOGs. */
function getArchivedOptions(options: ReadyPlanOption[]) {
  return options.filter((option) => !option.active || !option.optionGroup.active);
}

/** Creates stable RPOG models from the flat product offering option list. */
function createOptionGroups(options: ReadyPlanOption[]): ReadyPlanOptionGroup[] {
  const optionGroupsById = new Map<string, ReadyPlanOptionGroup>();
  for (const option of options) {
    const existing = optionGroupsById.get(option.optionGroup.id);
    if (existing) {
      existing.readyPlanOptions.push(option);
    } else {
      optionGroupsById.set(option.optionGroup.id, { ...option.optionGroup, readyPlanOptions: [option] });
    }
  }

  return [...optionGroupsById.values()]
    .sortBy((optionGroup) => optionGroup.order)
    .map((optionGroup) => ({
      ...optionGroup,
      readyPlanOptions: optionGroup.readyPlanOptions.sortBy((option) => option.order ?? Number.POSITIVE_INFINITY),
    }));
}

/** Groups RPOGs into form sections by checkout step. */
function createCheckoutStepSections(optionGroups: ReadyPlanOptionGroup[]) {
  const sectionsByStep = new Map<string, { title: string; optionGroups: ReadyPlanOptionGroup[]; sortOrder: number }>();
  for (const optionGroup of optionGroups) {
    const { code: stepCode, title } = getCheckoutStepSection(optionGroup);
    const section = sectionsByStep.get(stepCode) ?? { title, optionGroups: [], sortOrder: optionGroup.order };
    section.optionGroups.push(optionGroup);
    sectionsByStep.set(stepCode, section);
  }

  return [...sectionsByStep.values()]
    .sortBy((section) => section.sortOrder)
    .map((section) => ({ ...section, optionGroups: section.optionGroups.sortBy((optionGroup) => optionGroup.order) }));
}

/** Returns the form section for an RPOG checkout step. */
function getCheckoutStepSection(optionGroup: ReadyPlanOptionGroup) {
  const stepCode = optionGroup.step?.code;
  if (
    stepCode === CheckoutConfigOptionGroupStep.ExteriorElevation ||
    stepCode === CheckoutConfigOptionGroupStep.ExteriorDesignPackage
  ) {
    return { code: "EXTERIOR", title: "Exterior" };
  }
  return { code: stepCode ?? "OTHER", title: optionGroup.step?.name ?? "Other" };
}

/** Creates the selected RPO lookup for rendering SelectField values. */
function createSelectedOptionIdByGroupId(options: ReadyPlanOption[], selectedOptionIds: string[]) {
  const selectedOptionIdSet = new Set(selectedOptionIds);
  return options
    .filter((option) => selectedOptionIdSet.has(option.id))
    .sortBy((option) => [option.optionGroup.order, option.order ?? Number.POSITIVE_INFINITY])
    .reduce((map, option) => map.set(option.optionGroup.id, option.id), new Map<string, string>());
}

/** Returns the options that should be visible in an RPOG select field. */
function getOptionsForSelect(
  optionGroup: ReadyPlanOptionGroup,
  selectedOptionIds: string[],
  appliedOptionIds: string[],
) {
  const visibleInactiveOptionIds = new Set([...selectedOptionIds, ...appliedOptionIds]);
  return optionGroup.readyPlanOptions.filter((option) => option.active || visibleInactiveOptionIds.has(option.id));
}

/** Adds the applied cost suffix for options in the applied config. */
function getOptionLabel(
  option: ReadyPlanOption,
  appliedOptionIds: string[],
  appliedOptionCostById: Map<string, number>,
) {
  const optionName = option.displayName || option.name;
  if (!appliedOptionIds.includes(option.id)) return optionName;
  return `${optionName} (${formatToPrice({ valueInCents: appliedOptionCostById.get(option.id) ?? 0 })})`;
}

/** Returns RPO ids that cannot currently be selected. */
function getDisabledOptionIds(options: ReadyPlanOption[], selectedOptionIds: string[]) {
  const selectedOptionIdSet = new Set(selectedOptionIds);
  return options
    .filter((option) => {
      if (selectedOptionIdSet.has(option.id)) return false;
      const hasConflict = [...option.optionConflicts, ...option.optionConflictChildren].some((conflictingOption) =>
        selectedOptionIdSet.has(conflictingOption.id),
      );
      const hasPrereqs = option.optionPrerequisites
        .groupBy((prereq) => prereq.optionGroup.id)
        .toValues()
        .every((prereqsInGroup) => prereqsInGroup.some((prereq) => selectedOptionIdSet.has(prereq.id)));
      return !option.active || hasConflict || !hasPrereqs;
    })
    .map((option) => option.id);
}

/** Updates the selected option ids after one RPOG select changes. */
function updateSelectedOptionIdsForGroup(
  allOptions: ReadyPlanOption[],
  selectedOptionIds: string[],
  optionGroupId: string,
  optionId: string | undefined,
) {
  const optionIdsInGroup = new Set(
    allOptions.filter((option) => option.optionGroup.id === optionGroupId).map((option) => option.id),
  );
  const optionById = allOptions.keyByObject((option) => option.id);
  const nextOptionIds = selectedOptionIds.filter((optionId) => !optionIdsInGroup.has(optionId));
  if (optionId) nextOptionIds.push(optionId);
  return nextOptionIds.unique().sortBy((id) => {
    const option = optionById.get(id);
    return [option?.optionGroup.order ?? Number.POSITIVE_INFINITY, option?.order ?? Number.POSITIVE_INFINITY];
  });
}

/** Returns a submit disabled message when required applied RPOGs are missing. */
function getMissingRequiredRpos(optionGroups: ReadyPlanOptionGroup[], appliedOptionIds: string[]) {
  const appliedOptionIdSet = new Set(appliedOptionIds);
  const missingRpoGroups = optionGroups
    .filter(
      (optionGroup) =>
        optionGroup.required &&
        optionGroup.readyPlanOptions.some((option) => option.active) &&
        !optionGroup.readyPlanOptions.some((option) => appliedOptionIdSet.has(option.id)),
    )
    .map((optionGroup) => optionGroup.name);
  if (missingRpoGroups.isEmpty) return;
  return `${missingRpoGroups.join(", ")} missing a required option selection`;
}

/** Creates per-selected-option costs by grouping line items by their last RPOG order option. */
function createOptionCostById(result: ApplyProductOfferingConfig_ComputeResultFragment) {
  const optionById = createOptionById(result);
  const optionCostById = new Map<string, number>();
  for (const protoProjectItem of result.protoProjectItems) {
    const lineItems = [protoProjectItem.itemTemplateItem, ...protoProjectItem.modifyingTemplateItems].compact();
    for (const lineItem of lineItems) {
      const option = getLastOption(lineItem.options, optionById);
      if (!option) continue;
      optionCostById.set(option.id, (optionCostById.get(option.id) ?? 0) + lineItem.totalCostInCents);
    }
  }
  return optionCostById;
}

/** Creates an option lookup with RPOG order from a compute result. */
function createOptionById(result: ApplyProductOfferingConfig_ComputeResultFragment) {
  return result.readyPlan.optionGroups
    .flatMap((optionGroup) =>
      optionGroup.options.map(
        (option) => ({ ...option, groupOrder: optionGroup.order }) satisfies OptionWithGroupOrder,
      ),
    )
    .keyByObject((option) => option.id);
}

/** Finds the line item's last option by RPOG order. */
function getLastOption(
  options: readonly { id: string; displayName: string }[],
  optionById: Map<string, OptionWithGroupOrder>,
) {
  return options
    .map(
      (option) =>
        optionById.get(option.id) ?? {
          id: option.id,
          displayName: `Archived Option ${option.id}`,
          groupOrder: Number.MAX_SAFE_INTEGER,
        },
    )
    .sortBy((option) => option.groupOrder).last;
}

/** Converts option ids into the form-state list shape. */
function toFormReadyPlanOptions(optionIds: string[]) {
  return optionIds.map((readyPlanOptionId) => ({ readyPlanOptionId }));
}
